import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const cssCache = new WeakMap<HTMLElement, { gap: number; itemPadding: number }>(
  [],
);

/**
 * Reads gap and first-child padding from computed styles, cached per container
 * element so we only hit getComputedStyle once per container lifetime.
 */
function getContainerMetrics(
  container: HTMLElement,
  children: HTMLElement[],
): { gap: number; itemPadding: number } {
  let cached = cssCache.get(container);
  if (!cached) {
    const gap =
      parseInt(
        window.getComputedStyle(container).getPropertyValue('gap'),
        10,
      ) || 8;
    const itemPadding = children[0]
      ? parseInt(
          window.getComputedStyle(children[0]).getPropertyValue('padding-left'),
          10,
        ) || 16
      : 16;
    cached = { gap, itemPadding };
    cssCache.set(container, cached);
  }
  return cached;
}

/**
 * Calculates how many items fit within the container width using a two-pass
 * algorithm:
 *  1. Check if all items fit with no overflow indicator.
 *  2. If not, recalculate reserving space for the overflow indicator.
 *
 * @param container - The measurement container holding all items.
 * @param overflowIndicatorWidth - Pixel width of the overflow indicator (e.g.
 *   a "+N" badge, "See more" button, etc.). When omitted, the indicator width
 *   is estimated from the first item's padding.
 */
function calculateVisibleCount(
  container: HTMLElement,
  overflowIndicatorWidth?: number,
): number {
  const containerWidth = container.offsetWidth;
  const children = Array.from(container.children) as HTMLElement[];

  if (containerWidth === 0 || children.length === 0) {
    return 0;
  }

  const { gap, itemPadding } = getContainerMetrics(container, children);
  const indicatorWidth = overflowIndicatorWidth ?? 2 * itemPadding + 12; // 12px fallback for ~2-digit text

  const itemWidths = children.map((child) => child.offsetWidth);

  // First pass: can all items fit without any overflow indicator?
  let total = 0;
  let count = 0;
  for (let index = 0; index < itemWidths.length; index++) {
    const itemWidth = itemWidths[index] ?? 0;
    const next = total + (count > 0 ? gap : 0) + itemWidth;
    if (next <= containerWidth) {
      total = next;
      count++;
    } else {
      break;
    }
  }

  if (count >= itemWidths.length) {
    return count;
  }

  // Second pass: how many items fit while reserving space for the indicator?
  total = 0;
  count = 0;
  for (let index = 0; index < itemWidths.length; index++) {
    const itemWidth = itemWidths[index] ?? 0;
    const next = total + (count > 0 ? gap : 0) + itemWidth;
    const withOverflow =
      next + (count > 0 || index > 0 ? gap : 0) + indicatorWidth;
    if (withOverflow <= containerWidth) {
      total = next;
      count++;
    } else {
      break;
    }
  }

  return Math.max(1, count);
}

/**
 * Tracks how many items fit inside a flex row container, accounting for an
 * overflow indicator (e.g. "+N", "See more") when not all items fit.
 *
 * Works with any horizontal list of inline items — tags, chips, avatars, etc.
 *
 * Usage:
 *  1. Attach `measureRef` to a hidden div that always renders ALL items with
 *     the same gap and item styles as the visible container.
 *  2. Use `visibleCount` to decide how many items to render in the real
 *     container, and `itemCount - visibleCount` to drive the overflow indicator.
 *
 * @param itemCount - Total number of items in the list.
 * @param overflowIndicatorWidth - Pixel width of the overflow indicator. When
 *   omitted, it is estimated from the first item's padding (suitable for
 *   pill-shaped indicators with short text like "+N").
 */
export function useOverflowCount(
  itemCount: number,
  overflowIndicatorWidth?: number,
): {
  measureRef: RefObject<HTMLDivElement | null>;
  visibleCount: number;
} {
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  const measure = useCallback(() => {
    const container = measureRef.current;
    if (!container || container.offsetWidth === 0) {
      // Container not yet measured — fall back to showing all items so
      // visibleCount stays in sync with itemCount even when the container is
      // hidden or not yet laid out.
      setVisibleCount(itemCount);
      return;
    }
    setVisibleCount(
      itemCount === 0
        ? 0
        : calculateVisibleCount(container, overflowIndicatorWidth),
    );
  }, [itemCount, overflowIndicatorWidth]);

  // Synchronous measurement before first paint — prevents a flash of
  // partially-visible items on mount.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Re-measure whenever the container is resized so visibleCount stays in sync.
  // The ResizeObserver callback is throttled via requestAnimationFrame to avoid
  // triggering excessive re-renders during smooth resize animations.
  useEffect(() => {
    const container = measureRef.current;
    if (!container) {
      return;
    }

    let rafId = 0;
    const observer = new ResizeObserver(() => {
      rafId = requestAnimationFrame(measure);
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [measure]);

  return { measureRef, visibleCount };
}
